import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface MetaTagsProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  noIndex?: boolean;
  structuredData?: Record<string, any>;
}

export const MetaTags = ({
  title = 'MediQR - Digitale Notfallhilfe | by DiekerIT',
  description = 'Erstellen Sie personalisierte QR-Codes mit lebensrettenden Notfallinformationen für sich und Ihre Familie. Sofort verfügbar für Rettungskräfte im Ernstfall.',
  image = 'https://mediqr.de/mediqr-logo.png',
  type = 'website',
  noIndex = false,
  structuredData
}: MetaTagsProps) => {
  const location = useLocation();
  const currentUrl = `https://mediqr.de${location.pathname}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (property: string, content: string, isName = false) => {
      const attribute = isName ? 'name' : 'property';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description, true);
    
    // Open Graph tags
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:type', type);
    updateMetaTag('og:url', currentUrl);
    updateMetaTag('og:image', image);
    updateMetaTag('og:site_name', 'MediQR');
    updateMetaTag('og:locale', 'de_DE');

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', image, true);

    // Robots meta tag for preventing indexing
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow', true);
      updateMetaTag('googlebot', 'noindex, nofollow', true);
    } else {
      // Remove noindex if it exists
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute('content')?.includes('noindex')) {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = currentUrl;

    // Structured Data (JSON-LD)
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    // Cleanup function
    return () => {
      // Only clean up structured data on unmount
      if (structuredData) {
        const script = document.querySelector('script[type="application/ld+json"]');
        if (script) {
          script.remove();
        }
      }
    };
  }, [title, description, image, type, noIndex, currentUrl, structuredData]);

  return null;
};

// Structured data helpers
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "MediQR",
  "description": "Digitale Notfallhilfe durch personalisierte QR-Codes mit medizinischen Informationen",
  "url": "https://mediqr.de",
  "logo": "https://mediqr.de/mediqr-logo.png",
  "founder": {
    "@type": "Organization",
    "name": "DiekerIT"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Service",
    "availableLanguage": "German"
  }
});

export const getWebApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "MediQR",
  "description": "Erstellen Sie personalisierte QR-Codes mit lebensrettenden Notfallinformationen",
  "url": "https://mediqr.de",
  "applicationCategory": "HealthApplication",
  "operatingSystem": "Web Browser, iOS, Android",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "EUR"
  },
  "featureList": [
    "QR-Code Generierung",
    "Notfallinformationen speichern",
    "Medikamente und Allergien dokumentieren",
    "Notfallkontakte hinterlegen",
    "Familienprofile verwalten"
  ]
});

export const getHowToSchema = () => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "Wie erstelle ich einen MediQR Notfall-QR-Code?",
  "description": "Schritt-für-Schritt-Anleitung zur Erstellung eines persönlichen Notfall-QR-Codes",
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Registrierung",
      "text": "Erstellen Sie ein kostenloses Konto bei MediQR"
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "Profil erstellen",
      "text": "Geben Sie Ihre medizinischen Informationen, Allergien, Medikamente und Notfallkontakte ein"
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "QR-Code generieren",
      "text": "Ihr personalisierter QR-Code wird automatisch erstellt"
    },
    {
      "@type": "HowToStep",
      "position": 4,
      "name": "Ausdrucken und anbringen",
      "text": "Drucken Sie den QR-Code aus und bringen Sie ihn am Kühlschrank, im Portemonnaie oder an anderen wichtigen Stellen an"
    }
  ]
});

export const getFAQSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Was ist MediQR?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "MediQR ist ein digitaler Service zur Erstellung personalisierter QR-Codes mit wichtigen Notfallinformationen. Diese Codes können von Rettungskräften schnell gescannt werden, um auf lebenswichtige medizinische Informationen zuzugreifen."
      }
    },
    {
      "@type": "Question",
      "name": "Wie funktioniert MediQR?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sie erstellen ein Profil mit Ihren medizinischen Informationen, Allergien, Medikamenten und Notfallkontakten. MediQR generiert daraus einen QR-Code, den Sie ausdrucken und an wichtigen Stellen anbringen können. Im Notfall können Rettungskräfte den Code scannen und sofort auf Ihre Informationen zugreifen."
      }
    },
    {
      "@type": "Question",
      "name": "Sind meine Daten sicher?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ja, alle Daten werden verschlüsselt gespeichert und sind nur über den persönlichen QR-Code zugänglich. Profile können optional zusätzlich mit einem Passwort geschützt werden."
      }
    },
    {
      "@type": "Question",
      "name": "Ist MediQR kostenlos?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "MediQR bietet verschiedene Pläne an, darunter auch einen kostenlosen Basisplan. Premium-Pläne mit erweiterten Funktionen sind ebenfalls verfügbar."
      }
    }
  ]
});
